import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { usePostSEO } from "../hooks/useSEOData";
import { useFetch } from "../hooks/useFetch";
import { apiEndpoints, API_BASE_URL } from "../utils/api";
import { Breadcrumbs } from "../components/Breadcrumbs";

export const PostDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Cargar datos del post
  const {
    data: post,
    loading,
    error,
  } = useFetch(slug ? apiEndpoints.posts.single(slug) : null);

  // Construir URL para posts relacionados
  const buildRelatedPostsURL = () => {
    if (!post) return null;

    const categoryParam = post.category_id || post.category_slug;
    if (!categoryParam) return null;

    return apiEndpoints.posts.list(1, 4, categoryParam);
  };

  // Cargar posts relacionados
  const relatedPostsURL = buildRelatedPostsURL();
  const {
    data: relatedPostsResponse,
    loading: relatedLoading,
    error: relatedError,
  } = useFetch(relatedPostsURL, [post?.id]);

  // Procesar posts relacionados
  const getRelatedPosts = () => {
    if (!relatedPostsResponse) return [];

    let postsArray = [];

    if (relatedPostsResponse.success && relatedPostsResponse.data) {
      postsArray = relatedPostsResponse.data;
    } else if (Array.isArray(relatedPostsResponse)) {
      postsArray = relatedPostsResponse;
    } else if (
      relatedPostsResponse.data &&
      Array.isArray(relatedPostsResponse.data)
    ) {
      postsArray = relatedPostsResponse.data;
    }

    if (!Array.isArray(postsArray) || postsArray.length === 0) {
      return [];
    }

    // Filtrar el post actual
    const filteredPosts = postsArray.filter((relatedPost) => {
      return relatedPost.id !== post?.id && relatedPost.slug !== post?.slug;
    });

    return filteredPosts.slice(0, 3);
  };

  const relatedPosts = getRelatedPosts();

  // Obtener datos SEO del post usando el hook consolidado
  const seoData = usePostSEO(post);

  /*/ Debug SEO en desarrollo
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && seoData) {
      console.log('📊 SEO Data para PostDetail:', {
        title: seoData.title,
        description: seoData.description,
        image: seoData.image,
        url: seoData.url,
        keywords: seoData.keywords
      });
    }
  }, [seoData]);*/

  // Verificar si el post existe y redireccionar si es necesario
  useEffect(() => {
    if (error && error.status === 404) {
      const timer = setTimeout(() => {
        navigate("/", { replace: true });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error, navigate]);

  // Funciones auxiliares
  const processTags = (tags) => {
    if (!tags) return [];

    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
    }

    if (Array.isArray(tags)) {
      return tags.filter((tag) => tag && typeof tag === "string");
    }

    return [];
  };

  const calculateReadingTime = (content) => {
    if (!content) return null;

    const wordsPerMinute = 200;
    const textContent = content.replace(/<[^>]*>/g, "");
    const wordCount = textContent.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);

    return readingTime;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    const cleanBaseURL = API_BASE_URL.replace(/\/+$/, "");

    if (imagePath.startsWith("/uploads/")) {
      return `${cleanBaseURL}${imagePath}`;
    }

    return `${cleanBaseURL}/uploads/${imagePath}`;
  };

  if (loading) {
    return (
      <>
        <SEOHead
          title="Cargando..."
          description="Cargando contenido..."
          noindex={true}
        />
        <LoadingSkeleton />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <SEOHead
          title="Noticia no encontrada"
          description="La noticia que buscas no existe o ha sido eliminada."
          noindex={true}
        />
        <ErrorState error={error} />
      </>
    );
  }

  // Procesar datos del post
  const processedTags = processTags(post.tags);
  const readingTime =
    post.reading_time || calculateReadingTime(post.body || post.content);
  const featuredImage = getImageUrl(post.featured_image || post.image);

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    {
      name: post.category || post.category_name || "Noticias",
      url: `/categoria/${post.category_slug || "noticias"}`,
    },
    { name: post.title, url: `/post/${post.slug}` },
  ];

  const publishDate = new Date(post.published_at || post.created_at);
  const formattedDate = publishDate.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formattedTime = publishDate.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      {/* SEO Head con datos mejorados */}
      {seoData && (
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          image={seoData.image}
          url={seoData.url}
          type={seoData.type}
          article={seoData.article}
          author={seoData.author}
        />
      )}

      <div className="post-detail-page">
        {/* Header con imagen de fondo */}
        <header className="page-header">
          <div className="header-container-left">
            <div className="header-content-left">
              <div className="post-category-badge">
                <i className="fas fa-newspaper"></i>
                {post.category || post.category_name || "Noticias"}
              </div>

              <h1 className="post-title-header">{post.title}</h1>

              {/* Breadcrumbs dentro del header */}
              <div className="breadcrumbs-section-header">
                <Breadcrumbs items={breadcrumbItems} />
              </div>

              {/* Meta información en el header */}
              <div className="post-meta-header">
                <div className="meta-item">
                  <i className="fas fa-calendar"></i>
                  <time dateTime={publishDate.toISOString()}>
                    {formattedDate}
                  </time>
                </div>
                {(post.author || post.author_name) && (
                  <div className="meta-item">
                    <i className="fas fa-user"></i>
                    <span>{post.author || post.author_name}</span>
                  </div>
                )}
                {readingTime && (
                  <div className="meta-item">
                    <i className="fas fa-clock"></i>
                    <span>{readingTime} min de lectura</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          <div className="row">
            {/* Contenido principal */}
            <div className="col-lg-8">
              <article className="post-article">
                {/* Excerpt/Descripción */}
                {(post.excerpt || post.description || post.summary) && (
                  <div className="post-excerpt">
                    {post.excerpt || post.description || post.summary}
                  </div>
                )}

                {/* Imagen destacada */}
                {featuredImage && (
                  <figure className="post-image-container">
                    <img
                      src={featuredImage}
                      alt={post.title}
                      className="post-image"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    {(post.image_caption || post.caption) && (
                      <figcaption className="image-caption">
                        {post.image_caption || post.caption}
                      </figcaption>
                    )}
                  </figure>
                )}

                {/* Contenido del post */}
                <div className="post-content">
                  {post.body || post.content ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: post.body || post.content,
                      }}
                    />
                  ) : (
                    <div className="no-content">
                      <p>
                        El contenido de esta noticia no está disponible en este
                        momento.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {processedTags.length > 0 && (
                  <div className="post-tags">
                    <h4>Etiquetas:</h4>
                    <div className="tags-list">
                      {processedTags.map((tag, index) => (
                        <Link
                          key={index}
                          to={`/buscar?q=${encodeURIComponent(tag)}`}
                          className="tag"
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Información adicional del post */}
                <div className="post-footer">
                  <div className="post-dates">
                    <div className="publish-date">
                      <strong>Publicado:</strong> {formattedDate} a las{" "}
                      {formattedTime}
                    </div>
                    {post.updated_at && post.updated_at !== post.created_at && (
                      <div className="update-date">
                        <strong>Actualizado:</strong>{" "}
                        {new Date(post.updated_at).toLocaleDateString("es-ES", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Compartir en redes sociales */}
                <div className="post-share">
                  <h4>Compartir esta noticia:</h4>
                  <div className="share-buttons">
                    <ShareButton
                      platform="facebook"
                      url={seoData?.url || window.location.href}
                      title={seoData?.title || post.title}
                      image={seoData?.image}
                    />
                    <ShareButton
                      platform="twitter"
                      url={seoData?.url || window.location.href}
                      title={seoData?.title || post.title}
                      image={seoData?.image}
                    />
                    <ShareButton
                      platform="whatsapp"
                      url={seoData?.url || window.location.href}
                      title={seoData?.title || post.title}
                    />
                    <ShareButton
                      platform="linkedin"
                      url={seoData?.url || window.location.href}
                      title={seoData?.title || post.title}
                    />
                    <ShareButton
                      platform="copy"
                      url={seoData?.url || window.location.href}
                      title={seoData?.title || post.title}
                    />
                  </div>
                </div>
              </article>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <aside className="post-sidebar">
                {/* Posts relacionados */}
                <div className="sidebar-section">
                  <h3>
                    <i className="fas fa-newspaper"></i>
                    Noticias Relacionadas
                  </h3>

                  {/* Estado de carga */}
                  {relatedLoading && (
                    <div className="loading-related">
                      <div className="loading-spinner">
                        <i className="fas fa-spinner fa-spin"></i>
                      </div>
                      <p>Cargando noticias relacionadas...</p>
                    </div>
                  )}

                  {/* Estado de error */}
                  {relatedError && !relatedLoading && (
                    <div className="error-related">
                      <i className="fas fa-exclamation-triangle"></i>
                      <p>No se pudieron cargar las noticias relacionadas</p>
                    </div>
                  )}

                  {/* Posts relacionados exitosos */}
                  {!relatedLoading &&
                    !relatedError &&
                    relatedPosts.length > 0 && (
                      <div className="related-posts">
                        {relatedPosts.map((relatedPost) => (
                          <RelatedPostCard
                            key={`${relatedPost.id}-${relatedPost.slug}`}
                            post={relatedPost}
                          />
                        ))}

                        {/* Enlace para ver más de la categoría */}
                        {post.category_slug && (
                          <div className="view-more-category">
                            <Link
                              to={`/categoria/${post.category_slug}`}
                              className="view-more-btn"
                            >
                              <i className="fas fa-plus"></i>
                              Ver más de {post.category || post.category_name}
                            </Link>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Sin posts relacionados */}
                  {!relatedLoading &&
                    !relatedError &&
                    relatedPosts.length === 0 && (
                      <div className="no-related-posts">
                        <div className="no-posts-icon">
                          <i className="fas fa-newspaper"></i>
                        </div>
                        <h4>Sin noticias relacionadas</h4>
                        <p>No encontramos noticias similares en este momento</p>
                        <Link to="/" className="view-all-btn">
                          <i className="fas fa-home"></i>
                          Ver todas las noticias
                        </Link>
                      </div>
                    )}
                </div>

                {/* Información del autor */}
                {(post.author || post.author_name) && (
                  <div className="sidebar-section">
                    <h3>Sobre el Autor</h3>
                    <div className="author-info">
                      <div className="author-details">
                        <h4>{post.author || post.author_name}</h4>
                        {post.author_bio && <p>{post.author_bio}</p>}
                        {post.author_email && (
                          <a
                            href={`mailto:${post.author_email}`}
                            className="author-contact"
                          >
                            <i className="fas fa-envelope"></i>
                            Contactar
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>

        <style jsx>{`
          .post-detail-page {
            background: #f8fafc;
            min-height: 100vh;
            padding: 0 0 60px;
          }

          /* Header con imagen de fondo */
          .page-header {
            background: linear-gradient(
                to left,
                transparent 50%,
                rgba(0, 0, 0, 0.7) 100%
              ),
              url("https://img.freepik.com/free-photo/business-people-using-internet_53876-90850.jpg");
            background-size: cover;
            background-position: center;
            color: white;
            min-height: 280px;
            position: relative;
            display: flex;
            align-items: center;
            margin: 0;
          }

          .page-header::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.4);
          }

          .page-header .header-container-left {
            position: relative;
            z-index: 2;
            width: 100%;
            padding: 40px 40px 40px 40px;
          }

          .header-content-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            text-align: left;
            max-width: 800px;
          }

          .post-category-badge {
            display: flex;
            align-items: center;
            gap: 8px;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            padding: 8px 16px;
            border-radius: 25px;
            font-size: 0.9rem;
            font-weight: 600;
            margin-bottom: 20px;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          }

          .post-title-header {
            font-size: 2.8rem;
            font-weight: 800;
            margin: 0 0 16px 0;
            text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.6);
            line-height: 1.1;
            color: white;
          }

          .breadcrumbs-section-header {
            margin: 0 0 16px 0;
          }

          .breadcrumbs-section-header :global(nav) {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .breadcrumbs-section-header :global(.breadcrumb-link) {
            color: rgba(255, 255, 255, 0.9) !important;
            text-decoration: underline;
          }

          .breadcrumbs-section-header :global(.breadcrumb-link:hover) {
            color: white !important;
          }

          .breadcrumbs-section-header :global(.breadcrumb-current) {
            color: rgba(255, 255, 255, 0.8) !important;
            font-weight: 600;
          }

          .post-meta-header {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            align-items: center;
          }

          .meta-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.8rem;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.9);
            background: rgba(255, 255, 255, 0.15);
            padding: 6px 12px;
            border-radius: 15px;
            backdrop-filter: blur(5px);
          }

          .meta-item i {
            font-size: 0.85rem;
            opacity: 0.8;
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px 0;
          }

          .row {
            display: flex;
            gap: 30px;
            align-items: flex-start;
          }

          .col-lg-8 {
            flex: 2;
            min-width: 0;
          }

          .col-lg-4 {
            flex: 1;
            max-width: 350px;
            min-width: 300px;
          }

          .post-article {
            background: white;
            border-radius: 16px;
            padding: 40px 10px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            margin-bottom: 30px;
          }

          .post-excerpt {
            font-size: 1.2rem;
            color: #4b5563;
            line-height: 1.6;
            margin-bottom: 30px;
            padding: 20px;
            background: linear-gradient(135deg, #f9fafb, #f3f4f6);
            border-left: 4px solid #3b82f6;
            border-radius: 0 8px 8px 0;
            font-style: italic;
          }

          .post-image-container {
            margin: 0 0 30px 0;
          }

          .post-image {
            width: 100%;
            max-height: 500px;
            object-fit: cover;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          }

          .image-caption {
            font-size: 0.9rem;
            color: #6b7280;
            font-style: italic;
            text-align: center;
            margin-top: 12px;
            padding: 8px;
            background: #f9fafb;
            border-radius: 8px;
          }

          .post-content {
            line-height: 1.8;
            font-size: 1.1rem;
            color: #374151;
          }

          .post-content h2 {
            font-size: 1.8rem;
            font-weight: bold;
            margin: 30px 0 15px;
            color: #1f2937;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 8px;
          }

          .post-content h3 {
            font-size: 1.5rem;
            font-weight: bold;
            margin: 25px 0 12px;
            color: #1f2937;
          }

          .post-content p {
            margin-bottom: 20px;
          }

          .post-content blockquote {
            background: #f9fafb;
            border-left: 4px solid #10b981;
            margin: 20px 0;
            padding: 20px;
            border-radius: 0 8px 8px 0;
            font-style: italic;
            position: relative;
          }

          .no-content {
            text-align: center;
            padding: 40px;
            background: #fef3c7;
            border: 1px solid #fbbf24;
            border-radius: 8px;
            color: #92400e;
          }

          .post-tags {
            margin: 30px 0;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
          }

          .post-tags h4 {
            font-size: 1rem;
            color: #374151;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .tags-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .tag {
            background: linear-gradient(135deg, #e5e7eb, #d1d5db);
            color: #374151;
            padding: 6px 12px;
            border-radius: 16px;
            font-size: 0.85rem;
            text-decoration: none;
            transition: all 0.2s ease;
          }

          .tag:hover {
            background: linear-gradient(135deg, #3b82f6, #2563eb);
            color: white;
            transform: translateY(-1px);
          }

          .post-footer {
            margin: 30px 0;
            padding: 20px;
            background: #f9fafb;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }

          .post-dates {
            display: flex;
            flex-direction: column;
            gap: 8px;
            font-size: 0.9rem;
            color: #6b7280;
          }

          .post-share {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
          }

          .post-share h4 {
            font-size: 1rem;
            color: #374151;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .share-buttons {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
          }

          .post-sidebar {
            position: sticky;
            top: 100px;
          }

          .sidebar-section {
            background: white;
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 24px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .sidebar-section h3 {
            font-size: 1.1rem;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .loading-related,
          .error-related,
          .no-related-posts {
            text-align: center;
            padding: 20px;
            color: #6b7280;
          }

          .loading-spinner {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            margin-bottom: 8px;
          }

          .no-posts-icon {
            font-size: 2rem;
            color: #d1d5db;
            margin-bottom: 12px;
          }

          .no-related-posts h4 {
            color: #374151;
            margin-bottom: 8px;
          }

          .view-all-btn,
          .view-more-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: #3b82f6;
            color: white;
            text-decoration: none;
            border-radius: 6px;
            font-size: 0.85rem;
            font-weight: 500;
            margin-top: 12px;
            transition: background 0.2s ease;
          }

          .view-all-btn:hover,
          .view-more-btn:hover {
            background: #2563eb;
            color: white;
          }

          .view-more-category {
            text-align: center;
            padding: 16px;
            border-top: 1px solid #f3f4f6;
            margin-top: 16px;
          }

          .author-info {
            text-align: center;
          }

          .author-details h4 {
            color: #1f2937;
            margin-bottom: 8px;
          }

          .author-details p {
            color: #6b7280;
            font-size: 0.9rem;
            line-height: 1.5;
            margin-bottom: 12px;
          }

          .author-contact {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 16px;
            background: #f3f4f6;
            color: #374151;
            text-decoration: none;
            border-radius: 6px;
            font-size: 0.85rem;
            transition: all 0.2s ease;
          }

          .author-contact:hover {
            background: #3b82f6;
            color: white;
          }

          @media (max-width: 768px) {
            .page-header {
              min-height: 240px;
            }

            .page-header .header-container-left {
              padding: 24px 20px;
            }

            .post-title-header {
              font-size: 2rem;
            }

            .post-meta-header {
              gap: 8px;
            }

            .meta-item {
              font-size: 0.85rem;
              padding: 4px 8px;
            }

            .container {
              padding: 24px 16px 0;
            }

            .row {
              flex-direction: column;
            }

            .post-article {
              padding: 24px 0;
            }

            .post-excerpt {
              font-size: 1.1rem;
            }

            .share-buttons {
              justify-content: center;
            }
          }
        `}</style>
      </div>
    </>
  );
};

// Componente para posts relacionados
const RelatedPostCard = ({ post }) => {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    const cleanBaseURL = API_BASE_URL.replace(/\/+$/, "");
    return `${cleanBaseURL}/uploads/${imagePath}`;
  };

  const imageUrl = getImageUrl(post.featured_image || post.image);
  const publishDate = new Date(post.published_at || post.created_at);
  const excerpt = post.excerpt || post.description || post.summary || "";
  const truncatedExcerpt =
    excerpt.length > 100 ? excerpt.substring(0, 100) + "..." : excerpt;

  return (
    <article className="related-post-card">
      <div className="related-post-content">
        {/* Imagen del post */}
        <div className="related-post-image-container">
          <Link to={`/post/${post.slug}`} className="image-link">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={post.title}
                className="related-post-image"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="image-placeholder"
              style={{ display: imageUrl ? "none" : "flex" }}
            >
              <i className="fas fa-image"></i>
            </div>
          </Link>

          {/* Badge de categoría */}
          {(post.category_name || post.category) && (
            <div className="category-badge">
              {post.category_name || post.category}
            </div>
          )}
        </div>

        {/* Contenido textual */}
        <div className="related-post-text">
          <h4 className="related-post-title">
            <Link to={`/post/${post.slug}`}>{post.title}</Link>
          </h4>

          {truncatedExcerpt && (
            <p className="related-post-excerpt">{truncatedExcerpt}</p>
          )}

          {/* Meta información */}
          <div className="related-post-meta">
            <div className="meta-item">
              <i className="fas fa-calendar-alt"></i>
              <time dateTime={publishDate.toISOString()}>
                {publishDate.toLocaleDateString("es-ES", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </time>
            </div>

            {(post.author || post.author_name) && (
              <div className="meta-item">
                <i className="fas fa-user"></i>
                <span>{post.author || post.author_name}</span>
              </div>
            )}

            {post.views && (
              <div className="meta-item">
                <i className="fas fa-eye"></i>
                <span>{post.views} vistas</span>
              </div>
            )}
          </div>

          {/* Botón de lectura */}
          <Link to={`/post/${post.slug}`} className="read-more-link">
            <span>Leer artículo</span>
            <i className="fas fa-arrow-right"></i>
          </Link>
        </div>
      </div>

      <style jsx>{`
        .related-post-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 16px;
        }

        .related-post-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          border-color: #3b82f6;
        }

        .related-post-content {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .related-post-image-container {
          position: relative;
          height: 160px;
          overflow: hidden;
          background: #f9fafb;
        }

        .image-link {
          display: block;
          width: 100%;
          height: 100%;
          position: relative;
        }

        .related-post-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .related-post-card:hover .related-post-image {
          transform: scale(1.08);
        }

        .image-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 2.5rem;
        }

        .category-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
        }

        .related-post-text {
          padding: 16px;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .related-post-title {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 700;
          line-height: 1.3;
        }

        .related-post-title a {
          color: #1f2937;
          text-decoration: none;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          transition: color 0.2s ease;
        }

        .related-post-title a:hover {
          color: #3b82f6;
        }

        .related-post-excerpt {
          color: #6b7280;
          font-size: 0.85rem;
          line-height: 1.5;
          margin: 0 0 12px 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex-grow: 1;
        }

        .related-post-meta {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
          padding: 8px 0;
          border-top: 1px solid #f3f4f6;
        }

        .related-post-meta .meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: #6b7280;
          font-weight: 500;
        }

        .related-post-meta .meta-item i {
          font-size: 0.75rem;
          color: #9ca3af;
          width: 14px;
          text-align: center;
          flex-shrink: 0;
        }

        .read-more-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          color: #3b82f6;
          text-decoration: none;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
          margin-top: auto;
        }

        .read-more-link:hover {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border-color: #3b82f6;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
        }

        .read-more-link i {
          font-size: 0.75rem;
          transition: transform 0.2s ease;
        }

        .read-more-link:hover i {
          transform: translateX(2px);
        }
      `}</style>
    </article>
  );
};

// Componente para botones de compartir mejorado
const ShareButton = ({ platform, url, title, image }) => {
  const handleShare = async (event, platform, url, title, image) => {
    event.preventDefault();

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url
      )}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(title)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + " " + url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url
      )}`,
    };

    if (platform === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        const button = event.target.closest(".share-button");
        const originalHTML = button.innerHTML;
        button.innerHTML = '<i class="fas fa-check"></i><span>Copiado</span>';
        button.classList.add("copied");

        setTimeout(() => {
          button.innerHTML = originalHTML;
          button.classList.remove("copied");
        }, 2000);
      } catch (err) {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      return;
    }

    if (shareUrls[platform]) {
      window.open(
        shareUrls[platform],
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes"
      );
    }
  };

  const icons = {
    facebook: "fab fa-facebook-f",
    twitter: "fab fa-twitter",
    whatsapp: "fab fa-whatsapp",
    linkedin: "fab fa-linkedin-in",
    copy: "fas fa-copy",
  };

  const colors = {
    facebook: "#1877f2",
    twitter: "#1da1f2",
    whatsapp: "#25d366",
    linkedin: "#0077b5",
    copy: "#6b7280",
  };

  const labels = {
    facebook: "Facebook",
    twitter: "Twitter",
    whatsapp: "WhatsApp",
    linkedin: "LinkedIn",
    copy: "Copiar",
  };

  return (
    <button
      onClick={(e) => handleShare(e, platform, url, title, image)}
      className="share-button"
      style={{ backgroundColor: colors[platform] }}
      title={`Compartir en ${labels[platform]}`}
      data-platform={platform}
    >
      <i className={icons[platform]}></i>
      <span>{labels[platform]}</span>

      <style jsx>{`
        .share-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 8px;
          color: white;
          border: none;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .share-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          color: white;
        }

        .share-button.copied {
          background: #10b981 !important;
          transform: scale(1.05);
        }

        .share-button i {
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .share-button span {
            display: none;
          }

          .share-button {
            width: 40px;
            height: 40px;
            padding: 0;
            justify-content: center;
            border-radius: 50%;
          }
        }
      `}</style>
    </button>
  );
};

// Componentes auxiliares
const LoadingSkeleton = () => (
  <div className="container py-12">
    <div className="loading-container">
      <div className="loading-header">
        <div
          className="loading-skeleton"
          style={{ width: "200px", height: "20px", marginBottom: "20px" }}
        ></div>
        <div
          className="loading-skeleton"
          style={{ width: "100%", height: "60px", marginBottom: "20px" }}
        ></div>
        <div
          className="loading-skeleton"
          style={{ width: "80%", height: "30px", marginBottom: "30px" }}
        ></div>
      </div>
      <div
        className="loading-skeleton"
        style={{ width: "100%", height: "300px", marginBottom: "30px" }}
      ></div>
      <div className="loading-content">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="loading-skeleton"
            style={{ width: "100%", height: "20px", marginBottom: "15px" }}
          ></div>
        ))}
      </div>
    </div>

    <style jsx>{`
      .loading-container {
        background: white;
        border-radius: 16px;
        padding: 40px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }

      .loading-skeleton {
        background: linear-gradient(
          90deg,
          #f0f0f0 25%,
          #e0e0e0 50%,
          #f0f0f0 75%
        );
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
        border-radius: 8px;
      }

      @keyframes loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `}</style>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="container py-12">
    <div className="error-container">
      <div className="error-icon">
        <i className="fas fa-exclamation-triangle"></i>
      </div>
      <h1>Noticia no encontrada</h1>
      <p>
        {error?.status === 404
          ? "La noticia que buscas no existe o ha sido eliminada."
          : "Hubo un problema al cargar la noticia. Por favor, inténtalo de nuevo."}
      </p>
      <div className="error-actions">
        <Link to="/" className="btn btn-primary">
          <i className="fas fa-home"></i>
          Volver al inicio
        </Link>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-secondary"
        >
          <i className="fas fa-redo"></i>
          Intentar de nuevo
        </button>
      </div>
    </div>

    <style jsx>{`
      .error-container {
        text-align: center;
        padding: 60px 40px;
        background: white;
        border-radius: 16px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }

      .error-icon {
        font-size: 4rem;
        color: #f59e0b;
        margin-bottom: 20px;
      }

      .error-container h1 {
        color: #1f2937;
        margin-bottom: 16px;
      }

      .error-container p {
        color: #6b7280;
        font-size: 1.1rem;
        margin-bottom: 30px;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
      }

      .error-actions {
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
      }

      .btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        border-radius: 8px;
        font-weight: 500;
        text-decoration: none;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .btn-primary {
        background: #3b82f6;
        color: white;
      }

      .btn-primary:hover {
        background: #2563eb;
        color: white;
      }

      .btn-secondary {
        background: #6b7280;
        color: white;
      }

      .btn-secondary:hover {
        background: #4b5563;
      }
    `}</style>
  </div>
);
